import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./Scheduled.module.sass";
import Card from "../../../components/Card";
import Form from "../../../components/Form";
import Table from "./Table";
import Panel from "./Panel";
import { useNavigate } from "react-router-dom";

// Services
import { getStudentContractsAndConventions } from "../../../services/contract.service";

const ContractAndFiles = ({ studentId }) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [contracts, setContracts] = useState([]);
    const [filteredContracts, setFilteredContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedContracts, setSelectedContracts] = useState([]);

    useEffect(() => {
        const fetchContracts = async () => {
            try {
                setLoading(true);
                const result = await getStudentContractsAndConventions(studentId);
                
                // Transformer les contrats pour correspondre au format attendu par le composant Table
                const formattedContracts = result.items.map(contract => ({
                    id: contract.id,
                    product: contract.documentType === 'document' ? 'Contrat d\'apprentissage' : 'Convention de formation',
                    link: contract.cerfa_signature_status || 'En attente',
                    date: new Date(contract.start_date_of_contract || contract.created).toLocaleDateString(),
                    price: contract.opco_total_funded_amount || 0,
                    status: contract.opco_status || 'En attente',
                    company: contract.expand?.company_id?.name || 'Entreprise non spécifiée',
                    originalContract: contract // Garder les données originales pour référence
                }));
                
                setContracts(formattedContracts);
                setFilteredContracts(formattedContracts);
            } catch (error) {
                console.error('Erreur lors de la récupération des contrats:', error);
            } finally {
                setLoading(false);
            }
        };

        if (studentId) {
            fetchContracts();
        }
    }, [studentId]);

    // Fonction pour filtrer les contrats en fonction du texte de recherche
    const filterContracts = (searchText) => {
        if (!searchText.trim()) {
            // Si la recherche est vide, afficher tous les contrats
            setFilteredContracts(contracts);
            return;
        }
        
        const searchLower = searchText.toLowerCase();
        const filtered = contracts.filter(contract => {
            // Recherche dans le type de document (product)
            const typeMatch = contract.product.toLowerCase().includes(searchLower);
            
            // Recherche dans les données originales du contrat
            const companyName = contract.originalContract?.expand?.company_id?.name?.toLowerCase() || '';
            const opcoName = contract.originalContract?.opco_name?.toLowerCase() || '';
            const documentType = contract.originalContract?.documentType?.toLowerCase() || '';
            
            return typeMatch || 
                   companyName.includes(searchLower) || 
                   opcoName.includes(searchLower) ||
                   documentType.includes(searchLower);
        });
        
        setFilteredContracts(filtered);
    };
    
    // Effet pour filtrer les contrats lorsque la recherche change
    useEffect(() => {
        filterContracts(search);
    }, [search, contracts]);
    
    // Gestion de la sélection des contrats
    const handleSelectContract = (contractId, isSelected) => {
        if (isSelected) {
            // Ajouter le contrat à la sélection
            const contractToAdd = contracts.find(c => c.id === contractId);
            if (contractToAdd) {
                setSelectedContracts([...selectedContracts, contractToAdd.originalContract]);
            }
        } else {
            // Retirer le contrat de la sélection
            setSelectedContracts(selectedContracts.filter(c => c.id !== contractId));
        }
    };
    
    // Sélectionner ou désélectionner tous les contrats
    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            // Sélectionner tous les contrats filtrés
            const allOriginalContracts = filteredContracts.map(c => c.originalContract);
            setSelectedContracts(allOriginalContracts);
        } else {
            // Désélectionner tous les contrats
            setSelectedContracts([]);
        }
    };
    
    // Effacer la sélection
    const handleClearSelection = () => {
        setSelectedContracts([]);
    };
    
    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        // Assurons-nous que la recherche fonctionne correctement
        filterContracts(search);
    };

    return (
        <>
            <Card
                className={styles.card}
                classCardHead={styles.head}
                title="Contracts"
                classTitle={cn("title-purple", styles.title)}
                head={
                    <div className={styles.headerContainer}>
                        <Form
                            className={styles.form}
                            value={search}
                            setValue={setSearch}
                            onSubmit={handleSubmit}
                            placeholder="Rechercher par entreprise ou type de document"
                            type="text"
                            name="search"
                            icon="search"
                        />
                    </div>
                }
            >
                <div className={styles.wrapper}>
                    {selectedContracts.length > 0 && (
                        <div className={styles.actionButtons}>
                            {selectedContracts.length === 1 && (
                                <button 
                                    className={cn("button-small", styles.editButton)}
                                    onClick={() => navigate(`/contract/update/${selectedContracts[0].id}`)}
                                >
                                    Modifier
                                </button>
                            )}
                        </div>
                    )}
                    {loading ? (
                        <div className={styles.loading}>Chargement des contrats...</div>
                    ) : filteredContracts.length > 0 ? (
                        <Table 
                            items={filteredContracts} 
                            title="Contrats et conventions de l'étudiant" 
                            selectedContracts={selectedContracts.map(c => c.id)}
                            onSelectContract={handleSelectContract}
                            onSelectAll={handleSelectAll}
                        />
                    ) : contracts.length > 0 ? (
                        <div className={styles.empty}>Aucun contrat ne correspond à votre recherche</div>
                    ) : (
                        <div className={styles.empty}>Aucun contrat trouvé pour cet étudiant</div>
                    )}
                </div>
            </Card>
            <Panel 
                selectedContracts={selectedContracts} 
                onClearSelection={handleClearSelection} 
            />
        </>
    );
};

export default ContractAndFiles;
